import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Bell, BellRing, Route } from "lucide-react";
import { api, errMsg, eur } from "@/lib/api";
import { enablePush, pushEnabled, pushSupported } from "@/lib/push";
import { Loader, Chip, Empty, StatusBadge } from "@/components/common";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BookingRow } from "@/pages/Bookings";
import Onboarding from "@/pages/provider/Onboarding";
import ProviderInsurance from "@/pages/provider/ProviderInsurance";
import ProviderPayouts from "@/pages/provider/ProviderPayouts";

const STATUS = { draft: ["Entwurf", "neutral"], pending_review: ["In Prüfung durch Admin", "info"], approved: ["Freigeschaltet", "mint"], rejected: ["Abgelehnt", "error"], suspended: ["Gesperrt", "error"] };
const slotStart = (slot) => (slot && slot.includes("-") ? `ab ${slot.split("-")[0]}:00 Uhr` : slot);
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function AvatarUpload({ p, setP }) {
  const [busy, setBusy] = useState(false);
  const [v, setV] = useState(Date.now());
  const url = p.avatar_file_id ? `${BACKEND}/api/providers/${p.id}/avatar?v=${v}` : null;
  const initials = (p.display_name || "W").trim().slice(0, 2).toUpperCase();
  const onPick = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Bild zu groß (max. 5 MB)"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/provider/avatar", fd);
      setP({ ...p, avatar_file_id: "set" });
      setV(Date.now());
      toast.success("Profilbild aktualisiert");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <label className="relative group cursor-pointer shrink-0" data-testid="provider-avatar-upload">
      {url
        ? <img src={url} alt="Profilbild" className="w-16 h-16 rounded-2xl object-cover border border-line" data-testid="provider-avatar-img" />
        : <span className="flex items-center justify-center w-16 h-16 rounded-2xl bg-mint-soft text-mint-ink font-extrabold text-xl border border-mint" data-testid="provider-avatar-placeholder">{initials}</span>}
      <span className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/45 text-white text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity text-center px-1">
        {busy ? "…" : "Bild ändern"}
      </span>
      <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={onPick} disabled={busy} data-testid="provider-avatar-input" />
    </label>
  );
}

function PushCard() {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { pushEnabled().then(setOn).catch(() => {}); }, []);
  if (!pushSupported()) return null;
  const go = async () => {
    setBusy(true);
    try { await enablePush(); setOn(true); toast.success("Push aktiviert – du wirst sofort über neue Aufträge informiert."); }
    catch (e) { toast.error(e.message || errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="w-card p-4 flex items-center gap-3" data-testid="push-card">
      <span className="h-10 w-10 rounded-xl bg-mint-soft text-mint-ink grid place-items-center shrink-0">{on ? <BellRing size={20} aria-hidden="true" /> : <Bell size={20} aria-hidden="true" />}</span>
      <div className="flex-1 min-w-0">
        <p className="font-bold leading-tight">Sofort-Benachrichtigung</p>
        <p className="w-muted text-sm leading-tight">{on ? "Aktiv – neue Aufträge kommen direkt aufs Handy." : "Keine E-Mail abwarten: neue Aufträge direkt aufs Handy."}</p>
      </div>
      {!on && <button className="w-btn w-btn-primary !min-h-[42px] !px-4" onClick={go} disabled={busy} data-testid="push-enable-btn">{busy ? "…" : "Aktivieren"}</button>}
    </div>
  );
}

function PinAction({ b, path, label, onDone }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/bookings/${b.id}/${path}`, { pin }); toast.success(`${label} bestätigt`); onDone(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex gap-2 items-center">
      <input className="w-input !h-12 !w-32 text-xl tracking-[0.25em] font-bold text-center" inputMode="numeric" maxLength={6}
        value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="Code" data-testid={`today-${path}-input-${b.id}`} />
      <button className="w-btn w-btn-primary !min-h-[48px] flex-1" disabled={pin.length !== 6 || busy} data-testid={`today-${path}-btn-${b.id}`}>{label}</button>
    </form>
  );
}

function BtnAction({ b, status, label, onDone }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post(`/bookings/${b.id}/progress`, { status }); toast.success("Aktualisiert"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return <button className="w-btn w-btn-primary !min-h-[48px] w-full" onClick={go} disabled={busy} data-testid={`today-progress-${status}-${b.id}`}>{label}</button>;
}

function TodayCard({ b, reload, step }) {
  const addr = b.address ? `${b.address.street || ""}, ${b.address.plz || ""} ${b.address.city || ""}`.trim() : null;
  let action = null;
  if (b.status === "accepted") action = <Chip tone="warn">Wartet auf Zahlung des Kunden</Chip>;
  else if (b.status === "paid") action = <div><p className="w-label">Abholung – Code vom Kunden eingeben</p><PinAction b={b} path="handover" label="Abholen" onDone={reload} /></div>;
  else if (b.status === "picked_up") action = <BtnAction b={b} status="washing" label="Waschen gestartet" onDone={reload} />;
  else if (b.status === "washing") action = <BtnAction b={b} status="ready" label="Wäsche fertig" onDone={reload} />;
  else if (b.status === "ready") action = <div><p className="w-label">Rückgabe – Code vom Kunden eingeben</p><PinAction b={b} path="return" label="Zurückgeben" onDone={reload} /></div>;
  else if (b.status === "returned") action = <Chip tone="info">Wartet auf Bestätigung des Kunden</Chip>;
  return (
    <div className="w-card p-4 space-y-3" data-testid={`today-card-${b.id}`}>
      <div className="flex items-start gap-3">
        {step != null && <span className="h-7 w-7 rounded-full bg-mint text-mint-on grid place-items-center font-bold text-sm shrink-0 mt-0.5">{step}</span>}
        <div className="flex-1">
          <p className="font-bold">{b.customer_name} <span className="w-muted font-normal">· {b.code}</span></p>
          <p className="w-muted text-sm">{slotStart(b.slot)} · {b.kg} kg{b.self_pickup ? " · Selbstabholung" : ""}</p>
          {addr && <p className="w-muted text-sm flex items-center gap-1"><MapPin size={13} aria-hidden="true" /> {addr}</p>}
        </div>
        <StatusBadge status={b.status} />
      </div>
      {action}
    </div>
  );
}

function Today() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/bookings", { params: { as_provider: 1 } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  if (!items) return <Loader />;
  const today = new Date().toISOString().slice(0, 10);
  const map = {};
  items.filter((b) => ["paid", "picked_up", "washing", "ready"].includes(b.status) || b.date === today).forEach((b) => { map[b.id] = b; });
  const list = Object.values(map).sort((a, b) => (a.slot || "").localeCompare(b.slot || ""));
  const pending = list.filter((b) => ["paid", "ready"].includes(b.status)).length;
  return (
    <div className="space-y-4">
      <PushCard />
      <div className="grid grid-cols-2 gap-3">
        <div className="w-card p-4"><p className="w-label">Heute & offen</p><p className="text-3xl font-extrabold" data-testid="today-count">{list.length}</p></div>
        <div className="w-card p-4 bg-mint-soft"><p className="w-label">Aktion nötig</p><p className="text-3xl font-extrabold" data-testid="today-pending">{pending}</p></div>
      </div>
      {list.length ? (
        <div data-testid="today-list">
          <h2 className="text-lg font-bold mb-2 flex items-center gap-2"><Route size={18} aria-hidden="true" /> Deine Route heute</h2>
          <div className="space-y-3">{list.map((b, i) => <TodayCard key={b.id} b={b} reload={load} step={i + 1} />)}</div>
        </div>
      ) : <Empty>Nichts zu tun – lehn dich entspannt zurück.</Empty>}
    </div>
  );
}

function Orders() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/bookings", { params: { as_provider: 1 } }).then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  if (!items) return <Loader />;
  const open = items.filter((b) => b.status === "requested");
  const active = items.filter((b) => ["accepted", "paid", "picked_up", "washing", "ready", "returned", "disputed"].includes(b.status));
  const done = items.filter((b) => !open.includes(b) && !active.includes(b));
  const net = done.filter((b) => b.status === "completed").reduce((s, b) => s + b.price.total, 0);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-3">
        <div className="w-card p-4"><p className="w-label">Neue Anfragen</p><p className="text-3xl font-extrabold" data-testid="stat-open">{open.length}</p></div>
        <div className="w-card p-4"><p className="w-label">Aktiv</p><p className="text-3xl font-extrabold" data-testid="stat-active">{active.length}</p></div>
        <div className="w-card p-4"><p className="w-label">Umsatz brutto</p><p className="text-3xl font-extrabold">{eur(net)}</p></div>
      </div>
      {[["Neue Anfragen", open], ["Laufende Aufträge", active], ["Abgeschlossen", done]].map(([title, list]) => (
        <section key={title}><h2 className="text-lg font-bold mb-2">{title}</h2>{list.length ? <div className="space-y-2">{list.map((b) => <BookingRow key={b.id} b={b} />)}</div> : <Empty>Keine Einträge</Empty>}</section>
      ))}
    </div>
  );
}

export default function ProviderHome() {
  const [p, setP] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { api.get("/provider/me").then((r) => setP(r.data)).catch((e) => setError(errMsg(e))); }, []);
  if (error) return <div className="w-container py-10"><Empty>{error}</Empty></div>;
  if (!p) return <Loader />;
  const [label, tone] = STATUS[p.status] || STATUS.draft;
  const approved = p.status === "approved";
  return (
    <div className="w-container py-8 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4"><AvatarUpload p={p} setP={setP} /><div><p className="w-muted">Anbieterbereich</p><h1 className="text-3xl font-extrabold">{p.display_name}</h1></div></div>
        <div className="flex gap-2"><Chip tone={tone} testId="provider-status">{label}</Chip><Chip tone="info">Tier: {p.tier}</Chip></div>
      </div>
      {p.status === "rejected" && p.review_note && <p className="w-card p-4 bg-[var(--w-error-soft)]">Hinweis: {p.review_note}</p>}
      <Tabs defaultValue={approved ? "today" : "profile"}>
        <TabsList className="h-auto flex-wrap">
          {approved && <TabsTrigger value="today" className="text-base" data-testid="tab-today">Heute</TabsTrigger>}
          <TabsTrigger value="orders" className="text-base" data-testid="tab-orders">Aufträge</TabsTrigger>
          <TabsTrigger value="profile" className="text-base" data-testid="tab-profile">{approved ? "Profil & Leistungen" : "Onboarding"}</TabsTrigger>
          <TabsTrigger value="insurance" className="text-base" data-testid="tab-insurance">Versicherung</TabsTrigger>
          <TabsTrigger value="payouts" className="text-base" data-testid="tab-payouts">Auszahlungen</TabsTrigger>
        </TabsList>
        {approved && <TabsContent value="today" className="pt-4"><Today /></TabsContent>}
        <TabsContent value="orders" className="pt-4">{approved ? <Orders /> : <Empty>Aufträge sind nach Freigabe durch das Washly-Team verfügbar.</Empty>}</TabsContent>
        <TabsContent value="profile" className="pt-4"><Onboarding p={p} setP={setP} /></TabsContent>
        <TabsContent value="insurance" className="pt-4"><ProviderInsurance /></TabsContent>
        <TabsContent value="payouts" className="pt-4"><ProviderPayouts /></TabsContent>
      </Tabs>
    </div>
  );
}
